import * as React from 'react';
import { useAtomValue } from 'jotai';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';
import {
  Popover,
  Space,
  Col,
  DatePicker,
  Row,
  Input,
  Button,
  Checkbox,
  Divider,
  Select,
  type SelectProps,
} from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import * as atoms from '@components/filter/components/Management/atom';
import apptracker from '@utils/sensor/apptracker';
import * as EVENTS from '@src/utils/sensor/track';
import { Title, Footer } from './styled';

export type AdvancedSearchValue = {
  category?: string;
  subCategory?: string;
  createdBy?: string;
  createdOnStart?: string;
  createdOnEnd?: string;
  hiddenFilters?: boolean;
};

export interface FilterSearchBoxProps {
  defaultValue?: string;
  advancedSearch?: AdvancedSearchValue;
  hiddenEnabled?: boolean;
  onSearch?: (value: string) => void;
  onAdvancedSearch?: (value: AdvancedSearchValue) => void;
}

const FilterRow = ({ title, children }: { title?: string; children: React.ReactNode }) => {
  return (
    <Row align="middle" gutter={8} style={{ margin: '0 10px' }}>
      <Col span={6}>
        <Title>{title}</Title>
      </Col>
      <Col span={18}>{children}</Col>
    </Row>
  );
};

function useCleanedupState<T extends {}>(initialState: T | (() => T)) {
  const [state, setState] = React.useState(initialState);
  const cleanedUpState = React.useMemo(
    () =>
      Object.entries(state).reduce((prev, [k, v]) => {
        if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) {
          return prev;
        }
        return { ...prev, [k]: v };
      }, {} as T),
    [state],
  );

  return [cleanedUpState, setState] as const;
}

const FilterSearchBox = (props: FilterSearchBoxProps) => {
  const {
    defaultValue,
    advancedSearch = {},
    hiddenEnabled = false,
    onSearch,
    onAdvancedSearch,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [localValue, setLocalValue] = useCleanedupState(advancedSearch);
  const filterMeta = useAtomValue(atoms.filterMeta);
  const categoryOptions = React.useMemo(() => {
    const res: SelectProps['options'] = Object.entries(filterMeta.categories)
      .map(([category, subCategories]) => {
        return {
          label: category,
          title: category,
          options: subCategories.map((v) => ({ label: <span>{v}</span>, value: v, category })),
        };
      })
      .sort((a, b) => String(a.title).localeCompare(String(b.title)));

    res.unshift({
      label: 'ALL',
      value: 'ALL',
    });

    return res;
  }, [filterMeta.categories]);
  const handleSearch = React.useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        onSearch?.(value);
        apptracker(EVENTS.FILTER_MANAGEMENT_SAVED_FILTER_SEARCH, {
          advanced: false,
          content: value,
        });
      }, 300),
    [onSearch],
  );

  React.useEffect(() => {
    setLocalValue(advancedSearch);
  }, [advancedSearch, setLocalValue]);

  React.useEffect(() => {
    if (open) setLocalValue(advancedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Input.Search
      autoFocus
      defaultValue={defaultValue}
      placeholder="Search"
      allowClear
      style={{ maxWidth: 445 }}
      onChange={handleSearch}
      onSearch={onSearch}
      suffix={
        <Popover
          content={
            <Space direction="vertical" style={{ width: 400 }} size={10}>
              <FilterRow title="Category">
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="Select category"
                  defaultValue="ALL"
                  value={localValue.subCategory || 'ALL'}
                  options={categoryOptions}
                  onChange={(value, option) => {
                    setLocalValue((v) => ({
                      ...v,
                      category: (option as any)?.category || 'ALL',
                      subCategory: value,
                    }));
                  }}
                />
              </FilterRow>
              <FilterRow title="Created by">
                <Input
                  allowClear
                  placeholder="user@email.com"
                  value={localValue.createdBy}
                  onChange={(e) =>
                    setLocalValue((v) => ({
                      ...v,
                      createdBy: e.target.value,
                    }))
                  }
                />
              </FilterRow>
              <FilterRow title="Created date">
                <DatePicker
                  value={localValue.createdOnStart ? moment(localValue.createdOnStart) : null}
                  placeholder="Select start date"
                  style={{ width: '100%' }}
                  onChange={(_, dateString) => {
                    setLocalValue((v) => {
                      const next = { ...v, createdOnStart: dateString };
                      if (
                        dateString &&
                        next.createdOnEnd &&
                        moment(next.createdOnEnd).isBefore(moment(dateString), 'day')
                      ) {
                        delete next.createdOnEnd;
                      }

                      return next;
                    });
                  }}
                />
              </FilterRow>
              <FilterRow>
                <DatePicker
                  value={localValue.createdOnEnd ? moment(localValue.createdOnEnd) : null}
                  placeholder="Select end date"
                  disabledDate={(date) =>
                    !!localValue.createdOnStart &&
                    moment(localValue.createdOnStart).isAfter(date, 'day')
                  }
                  style={{ width: '100%' }}
                  onChange={(_, dateString) =>
                    setLocalValue((v) => ({ ...v, createdOnEnd: dateString }))
                  }
                />
              </FilterRow>
              {hiddenEnabled && (
                <FilterRow>
                  <Checkbox
                    checked={localValue.hiddenFilters}
                    onChange={(e) =>
                      setLocalValue((v) => ({
                        ...v,
                        hiddenFilters: e.target.checked,
                      }))
                    }
                  >
                    Hidden Filters
                  </Checkbox>
                </FilterRow>
              )}
              <Divider style={{ margin: '8px 0' }} />
              <Footer>
                <Button
                  type="text"
                  style={{ marginRight: 'auto' }}
                  onClick={() => setLocalValue(hiddenEnabled ? { hiddenFilters: true } : {})}
                >
                  Clear
                </Button>
                <Button
                  type="primary"
                  ghost
                  onClick={() => {
                    onAdvancedSearch?.(localValue);
                    setOpen(false);
                    apptracker(EVENTS.FILTER_MANAGEMENT_SAVED_FILTER_SEARCH, {
                      advanced: true,
                      content: localValue,
                    });
                  }}
                >
                  Search
                </Button>
                <Button
                  onClick={() => {
                    setLocalValue(advancedSearch);
                    setOpen(false);
                  }}
                >
                  Cancel
                </Button>
              </Footer>
            </Space>
          }
          placement="bottomRight"
          trigger="click"
          open={open}
          arrow={false}
          styles={{ body: { padding: '20px 0' } }}
          onOpenChange={setOpen}
        >
          <FilterOutlined
            style={{ color: isEmpty(localValue) ? 'rgba(0,0,0,.45)' : '#0046ff' }}
            onClick={() => setOpen(true)}
          />
        </Popover>
      }
    />
  );
};

export default FilterSearchBox;
